import React, { useState, useEffect, createContext } from 'react'
import app from "firebase/app"
import 'firebase/firestore'
import 'firebase/auth'
import 'firebase/storage'
import 'firebase/functions'
import { unstable_renderSubtreeIntoContainer } from 'react-dom'
import { nanoid } from 'nanoid'

const FirebaseContext = createContext(null)
export { FirebaseContext }

export default ({ children }) => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const isLocalhost = ['localhost', '127.0.0.1'].includes(hostname)
      || /^192\.168\./.test(hostname)
      || /^10\./.test(hostname)
    if (!app.apps.length) {
      app.initializeApp({
        apiKey: process.env.GATSBY_FB_API_KEY || (isLocalhost ? 'demo-api-key' : undefined),
        authDomain: process.env.GATSBY_FB_AUTH_DOMAIN || (isLocalhost ? 'test-project.firebaseapp.com' : undefined),
        databaseURL: process.env.GATSBY_FB_DB_URL,
        projectId: process.env.GATSBY_FB_PROJ_ID || (isLocalhost ? 'test-project' : undefined),
        storageBucket: process.env.GATSBY_FB_BUCKET || (isLocalhost ? 'test-project.appspot.com' : undefined),
        messagingSenderId: process.env.GATSBY_FB_MSG_ID || (isLocalhost ? '123456789' : undefined),
        appId: process.env.GATSBY_FB_APP_ID || (isLocalhost ? '1:123456789:web:local' : undefined),
        measurementId: process.env.GATSBY_FB_MEASUREMENT_ID
      })
      if (isLocalhost) {
          const emulatorHost = process.env.GATSBY_FIREBASE_EMULATOR_HOST || "127.0.0.1"
          const authPort = Number(process.env.GATSBY_AUTH_EMULATOR_PORT || 9099)
          const firestorePort = Number(process.env.GATSBY_FIRESTORE_EMULATOR_PORT || 8080)
          const functionsPort = Number(process.env.GATSBY_FUNCTIONS_EMULATOR_PORT || 5001)
          const storagePort = Number(process.env.GATSBY_STORAGE_EMULATOR_PORT || 9199)
          app.auth().useEmulator(`http://${emulatorHost}:${authPort}/`, { disableWarnings: true })
          app.firestore().useEmulator(emulatorHost, firestorePort)
          // Route callable HTTPS functions to the local emulator to avoid CORS in dev
          try {
            const functionsInstance = app.functions()
            if (typeof functionsInstance.useEmulator === 'function') {
              functionsInstance.useEmulator(emulatorHost, functionsPort)
            } else if (typeof functionsInstance.useFunctionsEmulator === 'function') {
              functionsInstance.useFunctionsEmulator(`http://${emulatorHost}:${functionsPort}`)
            }
          } catch (e) {
            // no-op
          }
          app.storage().useEmulator(emulatorHost, storagePort)
      }
    }


    const auth = useProvideAuth()

    return (
      <FirebaseContext.Provider value={ auth }>
        { children }
      </FirebaseContext.Provider>
    )
  }

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null)
  const [userPublic, setUserPublic] = useState(null)
  const usersDb = app.firestore().collection(`users`)

  const signout = () => {
    return app
      .auth()
      .signOut()
      .then(() => {
        setUser(false)
        setUserPublic(false)
      });
  };

  const signInAnonymouslyAndGetUser = async () => {
    // Returns the new user or if already signed in the current user
    const userResponse = await app.auth().signInAnonymously()
    return userResponse.user
  }


  // Subscribe to user on mount
  // Because this sets state in the callback it will cause any ...
  // ... component that utilizes this hook to re-render with the ...
  // ... latest auth object.
  useEffect(() => {
    const unsubscribe = app.auth().onAuthStateChanged(user => {
      console.log("authentication changed", user)
      if (user) {
        setUser(user);
      } else {
        setUser(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  

  
  useEffect(() => {
      if (!user || user.isAnonymous) {
        if(user === false) setUserPublic(false)
        return
      }

      loadOrCreatePublicUser(user)
  }, [user]);

  useEffect(() => {
    if (!userPublic || !userPublic.photoURL) {
      if(userPublic === false) localStorage.removeItem('signedInProfilePhoto')
      return
    }

    localStorage.setItem('signedInProfilePhoto', userPublic.photoURL)
}, [userPublic]);


  const findUserData = (user, key) => {
    if(!user) return

    if(user[key]) return user[key]

    if(user.providerData) {
        const providerDataRes = user.providerData.find(f => f[key])
        return providerDataRes && providerDataRes[key]
    }
}

const loadUserPublicDetailsFromUid = async uid => {
    const usersRes = await usersDb
    .where("uid", "==", uid)
      .get()
    if(usersRes.empty) return


  const doc = usersRes.docs[0]
  const username = doc.id
  const userPublicData = doc.data()
  const userPublic = {username, ...userPublicData}
  
  console.log("user data loaded: ", userPublicData)

  return userPublic
}

  const loadUserPublicDetails = async user => {
    return await loadUserPublicDetailsFromUid(user.uid)
  }

  const loadUserPublicDetailsFromUsername = async username => {
    const userRes = await usersDb.doc(username).get()
    if(!userRes.exists) return "not found"

    const userPublicData = userRes.data()

    return {username, ...userPublicData}
  }

  const generateUserName = async fullName => {
    let username = fullName.replace(' ', '')
     username = username.replace(/\s+/g, '')
     username = username.replace(/\'+/g, '')
     username = username.replace(/-+/g, '')
     const usernameLowercase = username.toLowerCase()
     const usersRes = await usersDb.doc(usernameLowercase).get()
     if(!usersRes.exists) return usernameLowercase
     return `${username.toLowerCase()}-${nanoid(6)}`

  }

  const createUserPublicDetails = async user => {
    const { uid } = user
    const photoURL = findUserData(user, "photoURL")  || '/assets/no-profile-picture-icon.png'
    const displayName = findUserData(user, "displayName")

    if(!displayName) {
      console.error("no displayName defined")
      return
    }
    const username = await generateUserName(displayName)

    const userPublic = {
      uid,
      photoURL,
      displayName
    }

    try {
      const doc = usersDb.doc(username)
      await doc.set(userPublic, { merge: true })
      
      console.log("User public created with id: ", doc.id)
      return userPublic
    }
    catch(error) {
        console.error("Error creating document: ", error);
    }
  }
  

  const loadOrCreatePublicUser = async user => {
    let userPublic = await loadUserPublicDetails(user)
    if(!userPublic) {
      userPublic = await createUserPublicDetails(user)
    }

    setUserPublic(userPublic)
  }

  const requireAuth = action => {
    if (!user || user.isAnonymous) {
      const destination = `${window.location.pathname}${window.location.search}${window.location.hash}`
      window.location = "/profile?redirect=" + encodeURIComponent(destination)
      return
    }
    
    action(user)
  }

  
  // Return the user object and auth methods
  return {
    app,
    user,
    userPublic,
    signout,
    signInAnonymouslyAndGetUser,
    loadUserPublicDetailsFromUsername,
    loadUserPublicDetailsFromUid,
    requireAuth
  };
}